/**
 * The set of languages and editor features this component can load.
 *
 * Every value is a thunk, so a consumer's bundler emits one chunk per entry and
 * fetches only the ones actually requested. Unrequested entries cost build time,
 * never load time — which is what satisfies "only the specified set loads".
 *
 * PATHS ARE 0.56-SPECIFIC. Monaco 0.56 restructured the package: the older
 * `basic-languages/<lang>/<lang>.contribution` and deep
 * `editor/contrib/**\/browser/*` paths that most guides still describe are gone
 * or superseded. 0.56 ships purpose-built per-language and per-feature entry
 * points, used below.
 *
 * Monaco lazy-loads grammars itself — `languages/definitions/sql/register` is a
 * stub calling `registerLanguage({ id: 'sql', loader: () => import('./sql.js') })`
 * — so these thunks nest inside Monaco's own laziness rather than duplicating it.
 */

export const LANGUAGES = {
  sql: () => import('monaco-editor/languages/definitions/sql/register'),
  python: () => import('monaco-editor/languages/definitions/python/register'),
  javascript: () => import('monaco-editor/languages/definitions/javascript/register'),
  shell: () => import('monaco-editor/languages/definitions/shell/register'),
  markdown: () => import('monaco-editor/languages/definitions/markdown/register'),
  yaml: () => import('monaco-editor/languages/definitions/yaml/register'),
} as const;

export const FEATURES = {
  suggest: () => import('monaco-editor/features/suggest/register'),
  hover: () => import('monaco-editor/features/hover/register'),
  bracketMatching: () => import('monaco-editor/features/bracketMatching/register'),
  find: () => import('monaco-editor/features/find/register'),
  wordHighlighter: () => import('monaco-editor/features/wordHighlighter/register'),
  toggleTabFocusMode: () => import('monaco-editor/features/toggleTabFocusMode/register'),
  folding: () => import('monaco-editor/features/folding/register'),
  links: () => import('monaco-editor/features/links/register'),
  parameterHints: () => import('monaco-editor/features/parameterHints/register'),
} as const;

export type BuiltinLanguageId = keyof typeof LANGUAGES;
export type FeatureId = keyof typeof FEATURES;

/**
 * Loaded when `features` is not supplied. Passing `features` replaces this
 * entirely rather than extending it.
 *
 * `toggleTabFocusMode` is here for accessibility, not convenience: Tab inserts a
 * tab character, so without this feature Ctrl+M (Ctrl+Shift+M on macOS) cannot
 * release focus and the editor is a WCAG 2.1.2 keyboard trap.
 */
export const DEFAULT_FEATURES: FeatureId[] = [
  'suggest',
  'hover',
  'bracketMatching',
  'find',
  'wordHighlighter',
  'toggleTabFocusMode',
];
